import { useCallback, useEffect, useState } from "react";
import { Trash2, Plus, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { staffApi, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", role: "pastor", notify: true };

export const StaffDirectory = ({ role }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(EMPTY);
  const isAdmin = role === "admin";

  const load = useCallback(() => { staffApi().get("/staff-members").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await staffApi().post("/staff-members", f); toast.success("Added to notification list"); setF(EMPTY); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (m) => {
    const { id, created_at, ...body } = m;
    try { await staffApi().put(`/staff-members/${id}`, { ...body, notify: !m.notify }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the list?`)) return;
    try { await staffApi().delete(`/staff-members/${m.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="staff-directory">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Pastors & staff</h2>
        <p className="text-sm text-slate-500">People with notifications on receive emails for new schedules, devotional uploads and visitor contact requests.</p>
      </div>
      {isAdmin ? (
        <form onSubmit={add} className="grid gap-3 rounded-3xl border border-slate-200 bg-white p-5 md:grid-cols-[1fr_1.3fr_160px_auto]" data-testid="staff-add-form">
          <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" />
          <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email-input" />
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="pastor">Pastor</SelectItem><SelectItem value="staff">Staff</SelectItem><SelectItem value="leader">Leader</SelectItem></SelectContent>
          </Select>
          <Button type="submit" className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="staff-add-button"><Plus className="mr-1 h-4 w-4" /> Add</Button>
        </form>
      ) : <p className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">Only an admin can edit this list.</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {list.length === 0 && <p className="text-sm text-slate-400" data-testid="staff-empty">No one added yet.</p>}
        {list.map((m) => (
          <div key={m.id} className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 sm:gap-4 sm:p-4" data-testid={`staff-member-${m.id}`}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#EBF3FA] font-display font-bold text-[#0A3A6B]">{m.name.slice(0, 1).toUpperCase()}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-900">{m.name} <span className="ml-1 text-xs capitalize text-slate-400">{m.role}</span></p>
              <p className="truncate text-sm text-slate-500">{m.email}</p>
            </div>
            {m.notify ? <Bell className="hidden h-4 w-4 shrink-0 text-[#1D64B4] sm:block" /> : <BellOff className="hidden h-4 w-4 shrink-0 text-slate-300 sm:block" />}
            {isAdmin && <Switch checked={m.notify} onCheckedChange={() => toggle(m)} data-testid={`staff-notify-${m.id}`} />}
            {isAdmin && <Button size="icon" variant="ghost" onClick={() => remove(m)} className="shrink-0 text-rose-500" data-testid={`staff-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
};
